import BluebirdPromise from 'bluebird';
import {
  OperationToQuickloadUrl,
  QuickLoad,
  getPreloadsFromPath,
  getQueryByOperationName,
} from '@trello/quickload';
import { loadApiDataFromUrl } from './loadApiDataFromUrl';
import { ModelCache } from './model-cache';
import { ninvoke } from 'app/scripts/lib/util/ninvoke';
import type { MappingRules } from './db.types';

const getPreloadOperationsForPath = () =>
  // split takes quickload:MemberHeader and gets operation name only
  getPreloadsFromPath().preloads?.map(
    ({ operationName }) => operationName.split(':')[1],
  ) || [];
// eslint-disable-next-line @trello/no-module-logic
let preloadOperationsForPath = getPreloadOperationsForPath();
const lastPathname = window.location.pathname;

export const loadApiDataFromQuickLoad = (
  queryName: string,
  {
    traceId,
    idModel,
    isHeaderLoad,
    modelType,
    mappingRules,
  }: {
    traceId?: string | null;
    idModel?: string;
    isHeaderLoad?: boolean | null;
    modelType: string;
    mappingRules?: MappingRules;
  },
) => {
  const operationDefinition = OperationToQuickloadUrl[queryName];

  const currentPathname = window.location.pathname;
  // we've already cleared quickload. Now we're transitioning to a new route
  // and it needs to be reinitialized
  if (
    preloadOperationsForPath.length === 0 &&
    lastPathname !== currentPathname
  ) {
    // header wont be called again, only the new data requests for the route.
    // so we can filter it out here.
    preloadOperationsForPath = getPreloadOperationsForPath().filter(
      (op) => op !== 'MemberHeader',
    );
  }

  if (!operationDefinition) {
    throw new Error(`${queryName} quickload definition was removed or renamed`);
  }

  if (!idModel) {
    const { param } = getPreloadsFromPath();
    if (operationDefinition.rootId === ':idMember') {
      idModel = 'me';
    } else if (param) {
      idModel = param;
    } else {
      // this can't really happen, unless someone passes the wrong idModel and there is no param
      // as a result. Throwing an error should stop this in development, but don't want to throw
      // in prod
      if (process.env.NODE_ENV !== 'production') {
        throw new Error(
          `Could not find param for ${queryName}. Please ensure correct idModel is being passed.`,
        );
      }

      return BluebirdPromise.resolve();
    }
  }

  let apiUrl = operationDefinition.url.replace(
    operationDefinition.rootId,
    idModel,
  );

  if (new RegExp('^/1/search').test(apiUrl)) {
    const dscToken = /dsc=([^;]+)/.exec(document.cookie)?.[1];
    // We probably shouldn't be setting this to the string "undefined", but this
    // was to preserve the functionality of the code after conversion to TypeScript.
    apiUrl += `&dsc=${dscToken ?? 'undefined'}`;
  }

  return loadApiDataFromUrl(apiUrl, {
    modelType,
    operationName: operationDefinition.operationName,
    traceId: traceId || QuickLoad.getPreloadTraceId(),
    isHeaderLoad: Boolean(isHeaderLoad),
  })
    .spread((data) => {
      if (data) {
        return ninvoke(
          ModelCache,
          'enqueueDelta',
          modelType,
          data,
          mappingRules,
          {
            document: getQueryByOperationName(queryName),
          },
        );
      } else {
        return [];
      }
    })
    .then((results) => {
      preloadOperationsForPath = preloadOperationsForPath.filter(
        (op) => op !== queryName,
      );

      if (preloadOperationsForPath.length === 0) {
        // we've loaded everything we need to into the model-cache
        QuickLoad.clear();
      }

      return results;
    });
};
